import { lazy, Suspense } from 'react'

import { defineMessages } from 'react-intl'
import { useRouteMatch } from 'react-router'

import { Page } from '@webapp/core/components/Page'
import { useAppRoutes, useProcurifyIntl } from '@webapp/hooks'
import { vendorReportConfig } from '@webapp/modules/reports/config/reportConfigs'
import { useVendorDetailHeaderPresenter } from '@webapp/modules/reports/features/get-vendor-detail-header/presenters/UseVendorDetailHeaderPresenter'
import { type ReportDetailsPageParams } from '../types'

const ReportDetailViewChunk = lazy(() =>
  import('../../feature-sets/ReportDetailView').then(
    ({ ReportDetailView }) => ({
      default: ReportDetailView,
    })
  )
)

const scope = `procurify.modules.reports.pages.OrdersByVendorReportDetailsPage`

const messages = defineMessages({
  pageTitle: {
    id: `${scope}.pageTitle`,
    defaultMessage: 'Orders by vendor',
  },
})

export const OrdersByVendorReportDetailsPage = () => {
  const intl = useProcurifyIntl()
  const { url } = useAppRoutes()
  const match = useRouteMatch<ReportDetailsPageParams>()

  const entityId = Number(match.params.id)

  // Breadcrumb entity name resolved independently of the line-item fetch
  // (BHV-022) — same query key as the Feature Set's header hook, so React
  // Query dedupes the fetch.
  const {
    data: { vendorName },
  } = useVendorDetailHeaderPresenter({ vendorId: entityId })

  return (
    <Page
      pageTitle={intl.formatMessage(messages.pageTitle)}
      breadcrumbs={[
        {
          link: url('OrdersByVendorReportPage'),
          name: intl.formatMessage(messages.pageTitle),
        },
        { name: vendorName ?? '', escapeCaseFormatting: true },
      ]}
    >
      <div data-testid='orders-by-vendor-report-details-page'>
        <Suspense fallback={null}>
          <ReportDetailViewChunk
            reportConfig={vendorReportConfig}
            entityId={entityId}
          />
        </Suspense>
      </div>
    </Page>
  )
}
